'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    question: "What is third-party logistics (3PL)?",
    answer: "Third-party logistics (3PL) refers to outsourcing your warehousing, distribution, and fulfillment needs to a specialized provider. Lindner Logistics offers tailored 3PL services in Milwaukee, helping businesses reduce overhead, improve efficiency, and scale operations without infrastructure investment."
  },
  {
    question: "Does Lindner Logistics offer 3PL warehouse services in Wisconsin?",
    answer: "Yes, Lindner Logistics provides comprehensive 3PL warehouse services across Wisconsin, including dry storage, cold storage, and freezer warehousing from our four state-of-the-art facilities."
  },
  {
    question: "How can a 3PL warehouse improve my supply chain efficiency?",
    answer: "A 3PL warehouse provides dedicated storage, inventory management, and streamlined order fulfillment, reducing lead times and overhead. They often use advanced WMS technology to give real-time visibility and help optimize stock levels."
  },
  {
    question: "What should I look for in a reliable 3PL provider?",
    answer: "Key factors include warehouse locations, range of services (cold storage, dry storage, fulfillment), scalability, technology capabilities, compliance certifications, and experience in your industry."
  },
  {
    question: "How much does 3PL warehousing cost?",
    answer: "3PL warehousing costs vary based on storage space needed, type of products (e.g., temperature-sensitive), fulfillment complexity, and service level agreements. Many providers offer custom quotes tailored to your business needs."
  },
  {
    question: "What industries typically use third-party logistics services?",
    answer: "Common industries include e-commerce, food and beverage, pharmaceuticals, retail, consumer goods, and manufacturing. 3PLs support businesses that need scalable warehousing and efficient distribution solutions."
  },
  {
    question: "What's the difference between a 3PL and a 4PL logistics provider?",
    answer: "A 3PL manages physical logistics services like warehousing and transportation. A 4PL oversees the entire supply chain, including managing multiple 3PLs, technology, and consulting services, acting as a single point of contact."
  },
  {
    question: "Are there 3PL warehouses near Milwaukee or Wisconsin?",
    answer: "Yes, the Milwaukee and greater Wisconsin area has several 3PL warehouses offering a range of services, from dry and cold storage to order fulfillment and value-added services. Proximity reduces shipping times and costs."
  },
  {
    question: "Can a 3PL help with ecommerce fulfillment?",
    answer: "Absolutely. Many 3PL providers specialize in ecommerce fulfillment, handling everything from inventory storage to pick, pack, and ship services. They often integrate with online marketplaces and shopping platforms."
  }
];

export function TPLFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
    rootMargin: '300px 0px',
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
            Frequently Asked Questions
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Third-Party Logistics FAQs
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            Get answers to common questions about third-party logistics and our 3PL warehouse services in Wisconsin.
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="max-w-4xl mx-auto"
        >
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', 0.05 * index)}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h4 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h4>
                  {openIndex === index ? (
                    <ChevronUp className="text-brand-600 flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-brand-600 flex-shrink-0" size={24} />
                  )}
                </button>
                
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6 pt-4"
                  >
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}